import type { PoiCategory } from '$lib/components/pois/types';
import type { Page } from '@sveltejs/kit';
import type { SearchParamsType } from './searchParams';
import { poiCategoryTrackerNames } from '$lib/components/pois/constants';
import {
	PROFILE,
	PROFILE_FAVORITES,
	ROUTE_GROUP_COSTS,
	ROUTE_GROUP_DETAILS,
	ROUTE_GROUP_EXPENSE_NOTE,
	ROUTE_GROUP_INSTRUCTIONS,
	ROUTE_GROUP_ON_THE_WAY,
	ROUTE_GROUP_ON_THE_WAY_POI,
	ROUTE_GROUP_SEARCH,
	ROUTE_GROUP_SEARCH_POI,
	ROUTE_GROUP_ZOI,
	ROUTE_GROUP_ZOI_POI,
	ROUTE_GROUP_ZOI_SETTINGS,
} from '$lib/constants/route';

export function isDatasheetPage(route: { id: string | null }) {
	return (route.id?.endsWith('/poi/[...poi]') || route.id?.endsWith('/poi')) ?? false;
}

export function isFiltersPage(route: { id: string | null }) {
	return route.id?.endsWith('/filters') ?? false;
}

export function isMapPoiListPage(route: { id: string | null }) {
	return route.id === '/(map)/map/[[category=poiCategory]]/[...address]';
}

export function isMapPageContext(route: { id: string | null }) {
	return route.id?.startsWith('/(map)/map') ?? false;
}

export function isRoutePageContext(route: { id: string | null }) {
	return route.id?.startsWith('/(map)/routes') ?? false;
}

export function isRoutePage(route: { id: string | null }) {
	return route.id === '/(map)/routes';
}
export function isRouteResultsPage(route: { id: string | null }) {
	return route.id === '/(map)/routes/results/[...addresses]';
}

export function isSearchCarPage(route: { id: string | null }) {
	return route.id === '/(map)/routes/search-car';
}

export function isRouteDetailsPageContext(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_DETAILS) ?? false;
}

export function isRoutePoiListPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_SEARCH;
}

export function isRoutePoiListPageContext(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_SEARCH) ?? false;
}

export function isMapDatasheetPage(route: { id: string | null }) {
	return route.id === '/(map)/map/[[category=poiCategory]]/poi/[...poi]';
}

export function isZoiPageContext(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_ZOI);
}

export function isZoiPoiPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_ZOI_POI;
}

export function isZoiPoiListPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_ZOI;
}

export function isRouteDetailsRecommendationPage(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_INSTRUCTIONS);
}

export function isTrafficPage(route: { id: string | null }) {
	return route.id === '/(map)/map/traffic/[...address]';
}

export function isZoiSettingsPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_ZOI_SETTINGS;
}

export function isCorridorPageContext(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_ON_THE_WAY) ?? false;
}

export function isCorridorPoiListPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_ON_THE_WAY;
}

export function isRoadsheetPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_INSTRUCTIONS;
}

export function isCostPage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_COSTS;
}

export function isExpenseNotePage(route: { id: string | null }) {
	return route.id === ROUTE_GROUP_EXPENSE_NOTE;
}

export function isRouteSearchPageContext(route: { id: string | null }) {
	return route.id?.startsWith(ROUTE_GROUP_SEARCH);
}

export function isHomePage(url: URL) {
	return url.pathname === '/';
}

export function isProfilePage(route: { id: string | null }) {
	return route.id?.startsWith(PROFILE);
}

export function isFavoritesPage(route: { id: string | null }) {
	return route.id === PROFILE_FAVORITES;
}

export function isRouteWithoutPOIs(route: { id: string | null }) {
	return isRouteResultsPage(route) || isRouteDetailsRecommendationPage(route) || isRoadsheetPage(route) || isCostPage(route) || isExpenseNotePage(route);
}

export const mapRouteUrlToPageName: Record<string, string> = {
	'/(map)/map/[[category=poiCategory]]/[...address]': 'poilist',
	'/(map)/map/[[category=poiCategory]]/poi/[...poi]': 'datasheet',
	'/(map)/routes': 'routesearch',
	'/(map)/routes/results/[...addresses]': 'routeresults',
	[ROUTE_GROUP_INSTRUCTIONS]: 'routeinstructions',
	[ROUTE_GROUP_COSTS]: 'routecosts',
	[ROUTE_GROUP_EXPENSE_NOTE]: 'routeexpensenotes',
	[ROUTE_GROUP_ZOI]: 'zoipoilist',
	[ROUTE_GROUP_ZOI_POI]: 'zoidatasheet',
	[ROUTE_GROUP_ON_THE_WAY]: 'corridorpoilist',
	[ROUTE_GROUP_ON_THE_WAY_POI]: 'corridordatasheet',
	[ROUTE_GROUP_SEARCH_POI]: 'routedatasheet',
	[ROUTE_GROUP_SEARCH]: 'routepoilist',
	'/(map)/map/traffic/[...address]': 'traffic',
};

function isPoiListWithOneCategory(category?: string, poiCategories?: PoiCategory[]) {
	return (poiCategories && poiCategories.length === 1) || category;
}

export function getPageName(page: Page, poiCategories?: PoiCategory[]) {
	const pageName = mapRouteUrlToPageName[page.route.id ?? ''] ?? 'undefined';

	const hasOneCategory
		= pageName === 'corridorpoilist' || isPoiListWithOneCategory(page.params.category, poiCategories);

	return pageName.includes('poilist') && !hasOneCategory ? 'homepage' : pageName;
}

export function getPageNameWithCategory(page: Page, searchParams: SearchParamsType | undefined) {
	const pageName = getPageName(page, searchParams?.poiCategories);
	const isSingleCategoryPoiList = isPoiListWithOneCategory(
		page.params.category,
		searchParams?.poiCategories,
	);
	const selectedPoiType = searchParams?.selectedPoi?.type;

	const category
		= (pageName === 'corridorpoilist' && 'accommodation')
		|| (isSingleCategoryPoiList && searchParams?.poiCategories?.[0])
		|| page.params.category
		|| (selectedPoiType && poiCategoryTrackerNames[selectedPoiType]);

	return `${(category && `${category.toLocaleLowerCase()}.`) || ''}${pageName}`;
}
